import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ListsProvider } from './contexts/ListsContext';
import { CollectionsProvider } from './contexts/CollectionsContext';
import { UserDesignationsProvider } from './contexts/UserDesignationsContext';
import { PhotoCacheProvider } from './contexts/PhotoCacheContext';
import { PlaceStatusCacheProvider } from './contexts/PlaceStatusCacheContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { NotificationModalProvider } from './contexts/NotificationModalContext';
import { TutorialProvider } from './contexts/TutorialContext';
import { TripPlannerTutorialProvider } from './contexts/TripPlannerTutorialContext';
import { ReorderModeProvider } from './contexts/ReorderModeContext';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import { LocationProvider } from './contexts/LocationContext';
import NotificationModal from './components/NotificationModal';
import { Tutorial, TutorialPrompt } from './components/Tutorial';
import InstallAppPrompt from './components/InstallAppPrompt';
import BottomNav from './components/BottomNav';
import OfflineBanner from './components/OfflineBanner';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import './App.css';

// Eagerly loaded pages (critical path - landing, login, main list views, and add place flows)
import Login from './pages/Login';
import Landing from './pages/Landing';
import MyLists from './pages/MyLists';
import ListDetail from './pages/ListDetail';
import Discover from './pages/Discover';
import AddPlace from './pages/AddPlace';
import AddPlaceFromSearch from './pages/AddPlaceFromSearch';
import SavePlace from './pages/SavePlace';
import PlaceDetails from './pages/PlaceDetails';
import NewList from './pages/NewList';
import Influencer from './pages/Influencer';
import MediaKit from './pages/MediaKit';

// Lazy loaded pages (deferred - less frequently accessed)
const Following = lazy(() => import('./pages/Following'));
const CityGuidePage = lazy(() => import('./pages/CityGuidePage'));
const Map = lazy(() => import('./pages/Map'));
const Notifications = lazy(() => import('./pages/Notifications'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Premium = lazy(() => import('./pages/Premium'));
const SharedListView = lazy(() => import('./pages/SharedListView'));
const Settings = lazy(() => import('./pages/Settings'));
const EditList = lazy(() => import('./pages/EditList'));
const DuplicateList = lazy(() => import('./pages/DuplicateList'));
const Admin = lazy(() => import('./pages/Admin'));
const Referrals = lazy(() => import('./pages/Referrals'));
const CreatorDashboard = lazy(() => import('./pages/CreatorDashboard'));
const ContentAnalyticsDetail = lazy(() => import('./pages/ContentAnalyticsDetail'));
const CreatorProfile = lazy(() => import('./pages/CreatorProfile'));
const UserPlaceListsPage = lazy(() => import('./pages/UserPlaceListsPage'));
const GoogleMapsTest = lazy(() => import('./components/GoogleMapsTest'));
const TripPlanner = lazy(() => import('./pages/TripPlanner'));

// Legal pages (rarely accessed, lazy load)
const LegalPages = lazy(() => import('./pages/legal').then(module => ({
  default: ({ page }) => {
    const Component = module[page];
    return Component ? <Component /> : null;
  }
})));

// Loading fallback for lazy-loaded routes
const PageLoading = () => (
  <div className="loading">Loading...</div>
);

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return user ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return !user ? children : <Navigate to="/lists" />;
}

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();

  // Pages where bottom nav should be hidden (startsWith check)
  const hideBottomNavPaths = ['/creator', '/shared', '/media-kit', '/c/'];
  // Pages where bottom nav should be hidden (endsWith check)
  const hideBottomNavSuffixes = ['/trip'];

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute>
              <Landing />
            </PublicRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/lists"
          element={
            <PrivateRoute>
              <MyLists />
            </PrivateRoute>
          }
        />
        <Route
          path="/list/:listId"
          element={
            <PrivateRoute>
              <ListDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/following"
          element={
            <PrivateRoute>
              <Following />
            </PrivateRoute>
          }
        />
        <Route
          path="/discover"
          element={
            <PrivateRoute>
              <Discover />
            </PrivateRoute>
          }
        />
        <Route
          path="/discover/city/:slug"
          element={
            <PrivateRoute>
              <CityGuidePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/map"
          element={
            <PrivateRoute>
              <Map />
            </PrivateRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <PrivateRoute>
              <Notifications />
            </PrivateRoute>
          }
        />
        <Route
          path="/user/:username"
          element={
            <PrivateRoute>
              <UserProfile />
            </PrivateRoute>
          }
        />
        <Route
          path="/user/:username/lists"
          element={
            <PrivateRoute>
              <UserPlaceListsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/premium"
          element={
            <PrivateRoute>
              <Premium />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />
        <Route
          path="/lists/new"
          element={
            <PrivateRoute>
              <NewList />
            </PrivateRoute>
          }
        />
        <Route
          path="/list/:listId/edit"
          element={
            <PrivateRoute>
              <EditList />
            </PrivateRoute>
          }
        />
        <Route
          path="/list/:listId/add-place"
          element={
            <PrivateRoute>
              <AddPlace />
            </PrivateRoute>
          }
        />
        <Route
          path="/list/:listId/duplicate"
          element={
            <PrivateRoute>
              <DuplicateList />
            </PrivateRoute>
          }
        />
        <Route
          path="/list/:listId/trip"
          element={
            <PrivateRoute>
              <TripPlanner />
            </PrivateRoute>
          }
        />
        <Route
          path="/place/save"
          element={
            <PrivateRoute>
              <SavePlace />
            </PrivateRoute>
          }
        />
        <Route
          path="/place/:placeId"
          element={
            <PrivateRoute>
              <PlaceDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/place/add"
          element={
            <PrivateRoute>
              <AddPlaceFromSearch />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <Admin />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/referrals"
          element={
            <PrivateRoute>
              <Referrals />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/creator"
          element={
            <PrivateRoute>
              <CreatorDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/creator/analytics"
          element={
            <PrivateRoute>
              <ContentAnalyticsDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/c/:username"
          element={<CreatorProfile />}
        />
        <Route
          path="/test-maps"
          element={<GoogleMapsTest />}
        />
        <Route
          path="/shared/:shareToken"
          element={<SharedListView />}
        />
        <Route
          path="/creator"
          element={<Influencer />}
        />
        <Route
          path="/media-kit"
          element={<MediaKit />}
        />
        {/* Legal Pages - accessible without authentication */}
        <Route path="/legal/privacy" element={<LegalPages page="PrivacyPolicy" />} />
        <Route path="/legal/terms" element={<LegalPages page="TermsOfService" />} />
        <Route path="/legal/cookies" element={<LegalPages page="CookiePolicy" />} />
        <Route path="/legal/ccpa" element={<LegalPages page="CCPANotice" />} />
      </Routes>
      {user &&
        !hideBottomNavPaths.some(path => location.pathname.startsWith(path)) &&
        !hideBottomNavSuffixes.some(suffix => location.pathname.endsWith(suffix)) &&
        <BottomNav />}
      <CookieConsent />
    </Suspense>
  );
}

function App() {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!googleMapsApiKey) {
    console.error('Google Maps API key is not set. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file');
    return (
      <div className="loading" style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Configuration Error</h2>
        <p>Google Maps API key is missing. Please check your .env file.</p>
      </div>
    );
  }

  // Google Maps is now loaded on-demand via GoogleMapsProvider
  // instead of blocking the entire app with LoadScript
  return (
    <GoogleMapsProvider apiKey={googleMapsApiKey}>
      <Router>
        <ScrollToTop />
        <AuthProvider>
          <UserDesignationsProvider>
            <SubscriptionProvider>
              <OfflineProvider>
                <ListsProvider>
                <CollectionsProvider>
                  <PhotoCacheProvider>
                    <PlaceStatusCacheProvider>
                      <NotificationModalProvider>
                      <LocationProvider>
                        <TutorialProvider>
                          <TripPlannerTutorialProvider>
                            <ReorderModeProvider>
                              <OfflineBanner />
                              <AppContent />
                              <NotificationModal />
                              <TutorialPrompt />
                              <Tutorial />
                              <InstallAppPrompt />
                            </ReorderModeProvider>
                          </TripPlannerTutorialProvider>
                        </TutorialProvider>
                      </LocationProvider>
                    </NotificationModalProvider>
                  </PlaceStatusCacheProvider>
                </PhotoCacheProvider>
              </CollectionsProvider>
              </ListsProvider>
            </OfflineProvider>
          </SubscriptionProvider>
        </UserDesignationsProvider>
        </AuthProvider>
      </Router>
    </GoogleMapsProvider>
  );
}

export default App;
